import React, { useState } from "react";
import Layout, { PageHeader } from "@finance/components/Layout";
import { useAuth } from "@finance/context/AuthContext";
import { useTheme } from "@finance/context/ThemeContext";
import { Button } from "@finance/components/ui/button";
import { Moon, Sun, DownloadSimple, Spinner } from "@phosphor-icons/react";
import { api } from "@finance/lib/api";
import { toast } from "sonner";

function downloadBlob(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function auditToCsv(d) {
  const rows = [];
  const push = (arr) => rows.push(arr.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","));
  push(["GIT / GRNI Reclassification Audit"]);
  push(["Generated", d.generated_at, "By", d.generated_by]);
  push(["DB", d.environment?.db_name, "Host", d.environment?.mongo_host, "Preview?", d.environment?.is_local_preview]);
  rows.push("");
  push(["RECONCILIATION BY PERIOD"]);
  push(["Period", "Old 2600 net", "GIT net", "GRNI net", "Ambiguous net", "Check (must be 0)"]);
  (d.reconciliation_by_period || []).forEach((r) => push([r.period, r.old_2600_net, r.git_net, r.grni_net, r.ambiguous_net, r.check]));
  rows.push("");
  push(["CLASSIFICATION TOTALS"]);
  push(["Class", "Entries", "Debit", "Credit", "Net (Cr-Dr)"]);
  Object.entries(d.classification_totals || {}).forEach(([k, v]) => push([k, v.n, v.dr, v.cr, v.net_cr_minus_dr]));
  rows.push("");
  push(["BY SOURCE TYPE"]);
  push(["Source", "Entries", "Debit", "Credit", "Net (Cr-Dr)"]);
  Object.entries(d.by_source_type || {}).forEach(([k, v]) => push([k, v.n, v.dr, v.cr, v.net_cr_minus_dr]));
  rows.push("");
  push(["GIT NET BY PO / SHIPMENT"]);
  push(["PO / Shipment", "Debit", "Credit", "Net (Cr-Dr)"]);
  (d.git_net_by_po || []).forEach((r) => push([r.po_or_shipment, r.dr, r.cr, r.net_cr_minus_dr]));
  rows.push("");
  push(["AMBIGUOUS ENTRIES", (d.ambiguous_entries || []).length]);
  push(["Date", "Source", "Debit", "Credit", "Memo"]);
  (d.ambiguous_entries || []).forEach((r) => push([r.date, r.source_type, r.dr, r.cr, r.memo]));
  rows.push("");
  push(["CLASSIFIED ENTRIES MISSING PO REF", (d.classified_missing_po_ref || []).length]);
  push(["Date", "Source", "Class", "Memo"]);
  (d.classified_missing_po_ref || []).forEach((r) => push([r.date, r.source_type, r.class, r.memo]));
  rows.push("");
  push(["SETTLEMENT DOUBLE-COUNT PROBE", d.settlement_double_count_probe?.count]);
  (d.settlement_double_count_probe?.items || []).forEach((r) => push([r.po, r.label, r.supplier]));
  rows.push("");
  push(["CHART OF ACCOUNTS"]);
  push(["Code", "Name", "Type", "Normal balance", "Active"]);
  (d.chart_of_accounts || []).forEach((a) => push([a.code, a.name, a.type, a.normal_balance, a.active]));
  push([]);
  push(["Free codes 2601-2699", (d.coa_hints?.free_codes_2601_2699 || []).join(" ")]);
  push(["Used liability codes in 26xx", (d.coa_hints?.liability_codes_in_2600_band_used || []).join(" ")]);
  return rows.join("\n");
}

export default function Profile() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const [loading, setLoading] = useState("");

  const runAudit = async (fmt) => {
    setLoading(fmt);
    try {
      const { data } = await api.get("/admin/git-grni-audit");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const env = data.environment?.is_local_preview ? "PREVIEW" : "PROD";
      if (data.environment?.is_local_preview) {
        toast.warning("Heads up: this is the PREVIEW database, not your Live production books.");
      }
      if (fmt === "csv") {
        downloadBlob(auditToCsv(data), `git-grni-audit_${env}_${stamp}.csv`, "text/csv;charset=utf-8;");
      } else {
        downloadBlob(JSON.stringify(data, null, 2), `git-grni-audit_${env}_${stamp}.json`, "application/json");
      }
      toast.success(`Audit downloaded (${env}).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run the audit.");
    } finally {
      setLoading("");
    }
  };

  return (
    <Layout>
      <PageHeader title="Profile & Settings" subtitle="Company details, your account and appearance" />
      <div className="p-8 space-y-6" data-testid="profile-page">
        <div className="flex flex-col items-center gap-3 rounded-sm border border-gray-200 bg-white p-8 text-center">
          <img src="/somcore-logo.png" alt="Somcore logo" className="h-20 w-auto object-contain" data-testid="profile-logo" />
          <div>
            <div className="font-display text-xl font-bold text-gray-900">Somcore Group</div>
            <div className="text-sm text-gray-500">Somcore Finance</div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-sm border border-gray-200 bg-white p-6">
            <div className="lx-label mb-3">Your Account</div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-gray-500">Name</dt><dd className="font-medium text-gray-900">{user?.name}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Role</dt><dd className="text-gray-900">{user?.role}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Email</dt><dd className="text-gray-900">{user?.email}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Access</dt><dd className="text-gray-900">{user?.is_owner ? "Owner" : user?.is_executive ? "Executive" : "Standard"}</dd></div>
            </dl>
          </div>

          <div className="rounded-sm border border-gray-200 bg-white p-6">
            <div className="lx-label mb-3">Appearance</div>
            <p className="mb-4 text-sm text-gray-500">Switch between light and dark mode. Your choice is remembered on this device.</p>
            <Button variant="outline" data-testid="theme-toggle-profile" onClick={toggle} className="gap-2">
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
              {theme === "dark" ? "Switch to Light mode" : "Switch to Dark mode"}
            </Button>
          </div>
        </div>

        {user?.is_owner && (
          <div className="rounded-sm border border-amber-200 bg-amber-50/40 p-6" data-testid="git-grni-audit-card">
            <div className="lx-label mb-1 text-amber-700">GIT / GRNI Reclassification Audit</div>
            <p className="mb-4 max-w-2xl text-sm text-gray-600">
              Read-only snapshot classifying every GL entry on account 2600 as Goods-in-Transit (import) vs GRNI (local),
              with a before/after reconciliation and your live Chart of Accounts. Nothing is changed — this only reads the books.
              The file header shows whether it came from your <b>Live production</b> database or preview.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button data-testid="audit-download-json" disabled={!!loading} onClick={() => runAudit("json")} className="gap-2 bg-[#1B4383] hover:bg-[#122E5C]">
                {loading === "json" ? <Spinner size={16} className="animate-spin" /> : <DownloadSimple size={16} />} Download JSON
              </Button>
              <Button data-testid="audit-download-csv" variant="outline" disabled={!!loading} onClick={() => runAudit("csv")} className="gap-2">
                {loading === "csv" ? <Spinner size={16} className="animate-spin" /> : <DownloadSimple size={16} />} Download CSV
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
